import {getTenantName} from 'common/HelperFunctions'
import SelectiveAiLoadingLogo from 'common/components/SelectiveAiLoadingLogo'
import Spinner from 'common/components/Spinner'
import {redirectToTenantWorkspaceIfNeeded} from 'common/helpers/authRedirect'
import {FC, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router'
import {REDUX_SET_ACCESS_TOKEN} from 'setup/redux/Actions/SignIn/ActionTypes/ReduxActionTypes'
import {resetDefaultPassword} from 'setup/redux/Actions/SignIn/forgotPassword'

import {toAbsoluteUrl} from '_metronic/helpers'

import {
  clearMustCreatePassword,
  isPasswordAlreadySetError,
} from '../selective/mustCreatePasswordHelpers'

// Safari/WebKit autofill override — inline background styles are ignored by
// the -webkit-autofill pseudo-class, so we use the inset box-shadow trick.
const autofillStyles = `
.cp-password-input:-webkit-autofill,
.cp-password-input:-webkit-autofill:hover,
.cp-password-input:-webkit-autofill:focus,
.cp-password-input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px rgba(55, 35, 35, 0.95) inset !important;
  box-shadow: 0 0 0 1000px rgba(55, 35, 35, 0.95) inset !important;
  -webkit-text-fill-color: #ffffff !important;
  caret-color: #ffffff !important;
  border-radius: 10px !important;
  transition: background-color 5000s ease-in-out 0s !important;
}

/* Mobile sizing. The layout is inline-styled (desktop values), which cannot
   carry media queries — hence these overrides. */
@media (max-width: 575.98px) {
  .cp-card {
    padding: 28px 20px !important;
    border-radius: 18px !important;
  }
  .cp-title {
    font-size: 22px !important;
  }
  .cp-submit {
    /* Was wrapping "Continue to Dashboard" onto two lines. */
    padding: 12px 16px !important;
    font-size: 14px !important;
    white-space: nowrap !important;
  }
}`
type Props = {
  onSuccess: () => void
}

const MIN_LENGTH = 8
const MAX_LENGTH = 20
const MODAL_MAX_WIDTH = 680
const LOADING_LOGO_SIZE = 88
// Brief success confirmation before auto-redirecting to the dashboard.
const REDIRECT_DELAY_MS = 2000

const Requirement: FC<{label: string; met: boolean}> = ({label, met}) => (
  <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
    <i
      className={met ? 'fas fa-check' : 'fas fa-times'}
      style={{fontSize: 11, color: met ? '#22c55e' : '#ef4444', width: 14, flexShrink: 0}}
    />
    <span style={{fontSize: 12, color: met ? '#22c55e' : '#ef4444'}}>{label}</span>
  </div>
)

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'rgba(55, 35, 35, 0.8)',
  border: 'none',
  borderRadius: 10,
  padding: '13px 44px 13px 16px',
  color: 'white',
  WebkitTextFillColor: 'white',
  fontSize: 15,
  outline: 'none',
  boxSizing: 'border-box',
}

const PasswordInput: FC<{
  value: string
  onChange: (v: string) => void
  show: boolean
  onToggle: () => void
  placeholder?: string
}> = ({value, onChange, show, onToggle, placeholder}) => {
  const passwordRef = useRef<HTMLInputElement>(null)
  const textRef = useRef<HTMLInputElement>(null)

  const handleToggle = () => {
    // Read the real DOM value from the currently visible input.
    // Password managers fill the DOM without firing React onChange,
    // so state can be stale / empty after autofill.
    const activeRef = show ? textRef : passwordRef
    if (activeRef.current) {
      const domVal = activeRef.current.value
      if (domVal && domVal !== value) onChange(domVal)
    }
    onToggle()
  }

  return (
    <div style={{position: 'relative'}}>
      {/* Two separate inputs so we never change `type` on an autofilled field.
          Browsers clear autofilled values when `type` is mutated. */}
      <input
        ref={passwordRef}
        type='password'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
        placeholder={placeholder}
        className='cp-password-input'
        autoComplete='new-password'
        style={{...inputStyle, display: show ? 'none' : 'block'}}
      />
      <input
        ref={textRef}
        type='text'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className='cp-password-input'
        autoComplete='off'
        style={{...inputStyle, display: show ? 'block' : 'none'}}
      />
      <button
        type='button'
        onClick={handleToggle}
        style={{
          position: 'absolute',
          right: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.55)',
          cursor: 'pointer',
          padding: 0,
          lineHeight: 1,
          zIndex: 10,
          WebkitAppearance: 'none',
        }}
      >
        <i className={show ? 'fas fa-eye-slash' : 'fas fa-eye'} style={{fontSize: 16}} />
      </button>
    </div>
  )
}

const CreatePassword: FC<Props> = ({onSuccess}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Fed to a type="hidden" autocomplete="username" input in the form so
  // password managers (Safari especially) associate the new password with
  // this account and prefill the user name in the "Save Password?" sheet.
  const accountEmail = useSelector(
    (state: any) => state.userReducer?.user?.email || state.userReducer?.user?.username || ''
  ) as string

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const hasMinLength = password.length >= MIN_LENGTH
  const hasMaxLength = password.length <= MAX_LENGTH
  const hasUppercase = /[A-Z]/.test(password)
  const hasNumber = /\d/.test(password)
  const hasSymbol = /[^A-Za-z0-9]/.test(password)

  const hasValidLength = hasMinLength && hasMaxLength

  const metCount = [hasValidLength, hasUppercase, hasNumber, hasSymbol].filter(Boolean).length
  const strengthPercent = (metCount / 4) * 100

  const strengthColor =
    metCount <= 1 ? '#ef4444' : metCount === 2 ? '#f59e0b' : metCount === 3 ? '#f59e0b' : '#22c55e'

  const isPasswordValid = hasValidLength && hasUppercase && hasNumber && hasSymbol
  const passwordsMatch = Boolean(password && confirmPassword && password === confirmPassword)
  const canSubmit = isPasswordValid && passwordsMatch && !loading

  const handleContinue = () => {
    if (!canSubmit) return
    setLoading(true)
    setError('')

    dispatch(
      resetDefaultPassword(
        password,
        (response: any) => {
          // The create-password API returns a fresh access token. The previous
          // default-password token is invalidated server-side (token-version
          // bump), so we must swap it in. Saving it to Redux persists it via
          // redux-persist (localforage), so the session survives refreshes and
          // browser restarts — keeping the user authenticated without a manual
          // re-login.
          const newAccessToken = response?.result?.accessToken
          if (newAccessToken) {
            dispatch({type: REDUX_SET_ACCESS_TOKEN, payload: newAccessToken})
          }

          setLoading(false)
          setIsSuccess(true)

          setTimeout(() => {
            // Clear the must-create-password flag (Redux + localStorage) so the
            // password guard lets the user through, then auto-login straight to
            // the dashboard. Do NOT navigate to /logout — that destroys the
            // session and would force a manual sign-in.
            onSuccess()
            const tenant = getTenantName()
            if (
              newAccessToken &&
              redirectToTenantWorkspaceIfNeeded(tenant, newAccessToken, {
                path: '/dashboard',
                searchParams: {step: '0'},
              })
            ) {
              return
            }
            navigate('/dashboard?step=0', {replace: true})
          }, REDIRECT_DELAY_MS)
        },
        (res: any) => {
          setLoading(false)
          if (isPasswordAlreadySetError(res)) {
            clearMustCreatePassword(dispatch)
            onSuccess()
            navigate('/dashboard?step=0', {replace: true})
            return
          }
          setError(res?.message ?? 'An error occurred. Please try again.')
        }
      )
    )
  }

  const SuccessView: FC = () => (
    <>
      <div
        style={{
          background: 'rgba(28, 23, 33, 0.95)',
          backdropFilter: 'blur(16px)',
          borderRadius: 24,
          padding: '60px 48px',
          width: '100%',
          maxWidth: MODAL_MAX_WIDTH,
          textAlign: 'center',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
        }}
      >
        <SelectiveAiLoadingLogo
          size={LOADING_LOGO_SIZE}
          alt='Redirecting to dashboard'
          className='mb-8'
          onDarkBackground
        />

        <h2
          style={{
            color: 'white',
            fontSize: 34,
            fontWeight: 700,
            marginBottom: 20,
            letterSpacing: '-0.01em',
          }}
        >
          Password Created Successfully!
        </h2>
        <p
          style={{
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 18,
            lineHeight: 1.6,
            margin: 0,
            fontWeight: 400,
          }}
        >
          Your password has been successfully created.
        </p>

        <div
          style={{
            marginTop: 28,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
          aria-live='polite'
        >
          <span style={{color: 'rgba(255, 255, 255, 0.55)', fontSize: 14, fontWeight: 500}}>
            Taking you to your dashboard...
          </span>
        </div>
      </div>
    </>
  )

  return (
    <div
      style={{
        position: 'fixed',
        // Leave room for the support/impersonation banner (App root) when present.
        top: 'var(--support-alert-height, 0px)',
        right: 0,
        bottom: 0,
        left: 0,
        background: 'linear-gradient(90.14deg, #0D0E12 20.07%, #431214 99.87%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        // Deliberately not `justifyContent: 'center'`: on short/mobile viewports
        // the form is taller than the screen, and a centred flex child overflows
        // equally in both directions — the top is cut off and unreachable by
        // scrolling. `margin: auto 0` on the wrapper below centres it when there
        // is room and falls back to scrollable top-down flow when there isn't.
        overflowY: 'auto',
        zIndex: 9999,
        padding: '24px 16px',
      }}
    >
      {/* Safari autofill override styles */}
      <style>{autofillStyles}</style>
      <div
        style={{
          margin: 'auto 0',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {isSuccess ? (
          <SuccessView />
        ) : (
          <>
            {/* Logo (outside the card) */}
            <div style={{textAlign: 'center', marginBottom: 24}}>
              <div style={{display: 'inline-flex', alignItems: 'center', gap: 10}}>
                <img
                  src={toAbsoluteUrl('/media/auth/image 70.png')}
                  alt='Logo'
                  style={{height: 34}}
                />
                <span
                  style={{color: 'white', fontWeight: 600, fontSize: 20, letterSpacing: '0.01em'}}
                >
                  TimeTable™
                </span>
              </div>
            </div>

            {/* Card */}
            <form
              className='cp-card'
              onSubmit={(e) => {
                e.preventDefault()
                handleContinue()
              }}
              style={{
                background: 'rgba(40, 30, 35, 0.9)',
                backdropFilter: 'blur(16px)',
                borderRadius: 24,
                border: '1.5px solid rgba(59, 130, 246, 0.55)',
                padding: '40px 48px',
                width: '100%',
                maxWidth: MODAL_MAX_WIDTH,
                boxShadow: '0 0 32px rgba(59, 130, 246, 0.12)',
              }}
            >
              {/* Title + subtitle */}
              <div style={{textAlign: 'center', marginBottom: 32}}>
                <h1
                  className='cp-title'
                  style={{
                    color: 'white',
                    fontSize: 26,
                    fontWeight: 700,
                    margin: 0,
                    letterSpacing: '-0.01em',
                  }}
                >
                  Set Your Password
                </h1>
                <p
                  style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: 13,
                    lineHeight: 1.5,
                    marginTop: 10,
                    marginBottom: 0,
                  }}
                >
                  Create a password so you can securely access your Da1TimeTable dashboard anytime.
                </p>
              </div>

              {/* Account email for the browser's password manager only — the
                  HTML-spec pattern for password-change forms: a type="hidden"
                  input with autocomplete="username" anchors the saved
                  credential to this account (Safari's "Save Password?" sheet
                  prefills the user name from it) without rendering anything.
                  Note: type="hidden", NOT a CSS-hidden text input — Safari
                  ignores visually-hidden fields but honors this one. */}
              {accountEmail && (
                <input type='hidden' name='username' autoComplete='username' value={accountEmail} />
              )}

              {/* Create Password */}
              <div style={{marginBottom: 24}}>
                <label
                  style={{
                    color: 'white',
                    fontWeight: 500,
                    fontSize: 15,
                    marginBottom: 10,
                    display: 'block',
                  }}
                >
                  Create Password <span style={{color: '#ef4444'}}>*</span>
                </label>

                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  show={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />

                {/* Strength bar */}
                {password && (
                  <div
                    style={{
                      marginTop: 8,
                      height: 4,
                      borderRadius: 4,
                      background: 'rgba(255,255,255,0.12)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${strengthPercent}%`,
                        background: strengthColor,
                        borderRadius: 4,
                        transition: 'width 0.3s ease, background 0.3s ease',
                      }}
                    />
                  </div>
                )}

                {/* Requirements */}
                {password && (
                  <div style={{marginTop: 12}}>
                    <div
                      style={{
                        color: 'rgba(255,255,255,0.65)',
                        fontSize: 12,
                        marginBottom: 8,
                      }}
                    >
                      Password must include:
                    </div>
                    <Requirement
                      label={`${MIN_LENGTH}-${MAX_LENGTH} characters`}
                      met={hasValidLength}
                    />
                    <Requirement label='At least one capital letter' met={hasUppercase} />
                    <Requirement label='At least one number' met={hasNumber} />
                    <Requirement label='At least one special character' met={hasSymbol} />
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  style={{
                    color: 'white',
                    fontWeight: 500,
                    fontSize: 15,
                    marginBottom: 10,
                    display: 'block',
                  }}
                >
                  Confirm Password <span style={{color: '#ef4444'}}>*</span>
                </label>

                <PasswordInput
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  show={showConfirm}
                  onToggle={() => setShowConfirm((v) => !v)}
                />

                {confirmPassword && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: passwordsMatch ? '#22c55e' : '#ef4444',
                    }}
                  >
                    {passwordsMatch ? 'Password match' : 'Passwords do not match'}
                  </div>
                )}
              </div>

              {/* API error */}
              {error && <div style={{marginTop: 14, fontSize: 13, color: '#ef4444'}}>{error}</div>}

              {/* Continue button — below fields (login-like placement) */}
              <button
                type='submit'
                className='cp-submit'
                disabled={!canSubmit}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: 10,
                  padding: '12px 28px',
                  fontSize: 15,
                  fontWeight: 600,
                  width: '100%',
                  marginTop: 20,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  opacity: canSubmit ? 1 : 0.65,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  transition: 'opacity 0.2s ease',
                }}
              >
                {loading ? (
                  <Spinner size='sm' type='border' />
                ) : (
                  <>
                    Continue to Dashboard
                    <i className='fas fa-arrow-right' style={{fontSize: 14}} />
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

export default CreatePassword
